import {
  FACE_HOLD_MS,
  FACE_RECOVER_FRAMES,
  FACE_SCORE_THRESHOLD,
  FACE_STALE_MS,
} from "../config/constants.ts";
import type { AppSettings, FaceDetectionBox, PrivacyFrameState, Rect } from "../utils/types.ts";
import {
  expandFaceBox,
  fallbackUpperRegion,
  isDetectionStale,
  maximumSafetyRegion,
  mirrorRect,
  shouldActivateFallback,
} from "./privacyMath.ts";

/**
 * Draws only privacy-processed pixels. The visible camera panel never receives
 * an unmasked frame. Work canvases stay detached from the document.
 */
export class FacePrivacyProcessor {
  private readonly work = document.createElement("canvas");
  private readonly temp = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D;
  private readonly tempCtx: CanvasRenderingContext2D;
  private lastSafeBoxes: Rect[] = [];
  private lastGoodAt = 0;
  private hadFace = false;
  private fallback = true;
  private recoverFrames = 0;
  private concealmentApplied = false;
  private lastMessage = "INITIALISING PRIVATE CAMERA…";
  private lastFaces = 0;

  constructor() {
    const ctx = this.work.getContext("2d", { willReadFrequently: false });
    const tempCtx = this.temp.getContext("2d", { willReadFrequently: false });
    if (!ctx || !tempCtx) {
      throw new Error("2D canvas is required for privacy processing.");
    }
    this.ctx = ctx;
    this.tempCtx = tempCtx;
  }

  reset(): void {
    this.lastSafeBoxes = [];
    this.lastGoodAt = 0;
    this.hadFace = false;
    this.fallback = true;
    this.recoverFrames = 0;
    this.concealmentApplied = false;
    this.lastMessage = "INITIALISING PRIVATE CAMERA…";
    this.lastFaces = 0;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.work.width, this.work.height);
    this.tempCtx.clearRect(0, 0, this.temp.width, this.temp.height);
  }

  process(
    video: HTMLVideoElement,
    faces: FaceDetectionBox[],
    settings: AppSettings,
    now: number,
    detectorError = false,
  ): PrivacyFrameState {
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    if (this.work.width !== width || this.work.height !== height) {
      this.work.width = width;
      this.work.height = height;
    }

    const ctx = this.ctx;
    ctx.save();
    if (settings.mirrorCamera) {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, width, height);
    ctx.restore();

    const reliable = faces.filter((face) => face.score >= FACE_SCORE_THRESHOLD);
    const mirrored = reliable.map((face) => {
      const expanded = expandFaceBox(face.box, width, height);
      return settings.mirrorCamera ? mirrorRect(expanded, width) : expanded;
    });

    const stale = isDetectionStale(this.lastGoodAt, now, FACE_STALE_MS);
    const wantsFallback = shouldActivateFallback({
      hadFace: this.hadFace,
      hasReliableFace: mirrored.length > 0,
      isStale: stale,
      detectorError,
    });

    if (mirrored.length > 0 && !detectorError) {
      this.hadFace = true;
      this.lastGoodAt = now;
      this.lastSafeBoxes = mirrored;
      this.recoverFrames += 1;
      if (this.recoverFrames >= FACE_RECOVER_FRAMES) this.fallback = false;
    } else {
      this.recoverFrames = 0;
      if (wantsFallback) this.fallback = true;
    }

    const useHeldBoxes =
      mirrored.length === 0 &&
      this.lastSafeBoxes.length > 0 &&
      now - this.lastGoodAt <= FACE_HOLD_MS &&
      !this.fallback;

    const boxesToMask = mirrored.length > 0 ? mirrored : useHeldBoxes ? this.lastSafeBoxes : [];
    const maxSafety = settings.privacyMode === "maximum-safety";

    if (maxSafety) {
      this.concealRect(maximumSafetyRegion(width, height), settings);
    }
    if (this.fallback) {
      this.concealRect(fallbackUpperRegion(width, height), settings);
    }
    for (const box of boxesToMask) {
      this.concealRect(box, settings);
    }

    this.concealmentApplied = true;
    this.lastFaces = boxesToMask.length;
    this.lastMessage = this.fallback ? "PRIVACY FALLBACK ACTIVE." : "FACE CONCEALED";
    return this.getState(now);
  }

  drawTo(target: CanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number): void {
    target.drawImage(this.work, dx, dy, dw, dh);
  }

  getCanvas(): HTMLCanvasElement {
    return this.work;
  }

  getState(now = performance.now()): PrivacyFrameState {
    return {
      ready: this.concealmentApplied,
      fallbackActive: this.fallback,
      facesMasked: this.lastFaces,
      lastSafeAgeMs: this.lastGoodAt ? now - this.lastGoodAt : Number.POSITIVE_INFINITY,
      message: this.lastMessage,
      concealmentApplied: this.concealmentApplied,
    };
  }

  private concealRect(rect: Rect, settings: AppSettings): void {
    if (rect.width < 2 || rect.height < 2) return;
    const strength = Math.max(1, settings.concealmentStrength);
    const type = settings.concealmentType;
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.ellipse(cx, cy, rect.width * 0.52, rect.height * 0.52, 0, 0, Math.PI * 2);
    this.ctx.clip();
    if (type === "pixelate" || type === "both") {
      const blocksX = Math.max(2, Math.round(rect.width / (8 + strength * 5)));
      const blocksY = Math.max(2, Math.round(rect.height / (8 + strength * 5)));
      this.temp.width = blocksX;
      this.temp.height = blocksY;
      this.tempCtx.imageSmoothingEnabled = false;
      this.tempCtx.clearRect(0, 0, blocksX, blocksY);
      this.tempCtx.drawImage(
        this.work,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        blocksX,
        blocksY,
      );
      this.ctx.imageSmoothingEnabled = false;
      this.ctx.drawImage(this.temp, rect.x, rect.y, rect.width, rect.height);
    }
    if (type === "blur" || type === "both") {
      const extra = type === "both" ? 6 : 0;
      this.ctx.save();
      this.ctx.filter = `blur(${14 + strength * 2.6 + extra}px)`;
      this.ctx.drawImage(
        this.work,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
      );
      this.ctx.restore();
    }
    const gradient = this.ctx.createRadialGradient(
      cx,
      cy,
      Math.min(rect.width, rect.height) * 0.28,
      cx,
      cy,
      Math.max(rect.width, rect.height) * 0.62,
    );
    gradient.addColorStop(0, "rgba(4, 10, 22, 0.18)");
    gradient.addColorStop(0.7, "rgba(4, 10, 22, 0.08)");
    gradient.addColorStop(1, "rgba(4, 10, 22, 0)");
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.ellipse(cx, cy, rect.width * 0.52, rect.height * 0.52, 0, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();
  }
}
